/** @module hooks/useGooglePlacesAutocomplete */

import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  AddressSuggestion,
  Coordinates,
  LocationBias,
  LocationRestriction,
  UseGooglePlacesAutocompleteOptions,
  UseGooglePlacesAutocompleteReturn,
} from '../types';

import {
  AUTOCOMPLETE_ENDPOINT,
  CACHE_TTL_MS,
  DEBOUNCE_MS,
  DEFAULT_INCLUDED_PRIMARY_TYPES,
  DEFAULT_LOCATION_RESTRICTION,
  DEFAULT_REGION_CODE,
  GOOGLE_MAPS_API_KEY,
  MAX_CACHE_SIZE,
  MIN_QUERY_LENGTH,
  PLACE_DETAILS_ENDPOINT,
  getDefaultLabels,
} from '../constants';

// ── Internal error class ────────────────────────────────────────────────────

class GoogleApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'GoogleApiError';
    this.status = status;
  }

  get isQuotaError(): boolean {
    return this.status === 429 || this.status === 403;
  }
}

// ── Internal API payload types ──────────────────────────────────────────────

type SuggestionPayload = {
  suggestions?: Array<{
    placePrediction?: {
      placeId?: string;
      text?: { text?: string };
      structuredFormat?: {
        mainText?: { text?: string };
        secondaryText?: { text?: string };
      };
    };
  }>;
};

type PlaceDetailsPayload = {
  formattedAddress?: string;
  location?: {
    latitude?: number;
    longitude?: number;
  };
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const isWithinRestriction = (
  latitude: number,
  longitude: number,
  restriction: LocationRestriction,
): boolean => {
  const { low, high } = restriction.rectangle;
  return latitude >= low.latitude
    && latitude <= high.latitude
    && longitude >= low.longitude
    && longitude <= high.longitude;
};

const parseSuggestions = (payload: SuggestionPayload): AddressSuggestion[] => {
  if (!payload?.suggestions) return [];

  return payload.suggestions.reduce<AddressSuggestion[]>((acc, suggestion) => {
    const prediction = suggestion.placePrediction;
    const placeId = prediction?.placeId ?? '';
    const fallback = prediction?.text?.text ?? '';
    const primary = prediction?.structuredFormat?.mainText?.text ?? fallback;
    const secondary = prediction?.structuredFormat?.secondaryText?.text ?? '';
    const fullText = fallback || (secondary ? `${primary}, ${secondary}` : primary);

    if (!placeId || !fullText) return acc;

    acc.push({
      id: placeId,
      primaryText: primary || fullText,
      secondaryText: secondary || undefined,
      fullText,
    });
    return acc;
  }, []);
};

const fetchSuggestions = async (
  apiKey: string,
  input: string,
  languageCode: string,
  sessionToken: string,
  locationRestriction: LocationRestriction,
  includedPrimaryTypes: string[],
  regionCode: string,
  locationBias?: LocationBias,
  signal?: AbortSignal,
): Promise<AddressSuggestion[]> => {
  const response = await fetch(AUTOCOMPLETE_ENDPOINT, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': [
        'suggestions.placePrediction.placeId',
        'suggestions.placePrediction.text',
        'suggestions.placePrediction.structuredFormat',
      ].join(','),
    },
    body: JSON.stringify({
      input,
      languageCode,
      sessionToken,
      regionCode,
      // Only include type filter when explicitly provided.
      ...(includedPrimaryTypes.length > 0 && { includedPrimaryTypes }),
      // Mutually exclusive — locationBias takes priority when provided.
      ...(locationBias
        ? { locationBias }
        : { locationRestriction }),
    }),
  });

  if (!response.ok) throw new GoogleApiError('Address suggestions request failed', response.status);
  return parseSuggestions(await response.json() as SuggestionPayload);
};

const fetchPlaceDetails = async (
  apiKey: string,
  id: string,
  languageCode: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<{ address: string; coordinates: Coordinates } | null> => {
  const params = new URLSearchParams({ languageCode, sessionToken });

  const response = await fetch(`${PLACE_DETAILS_ENDPOINT}/${encodeURIComponent(id)}?${params}`, {
    method: 'GET',
    signal,
    headers: {
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': 'formattedAddress,location',
    },
  });

  if (!response.ok) throw new GoogleApiError('Place details request failed', response.status);

  const payload = await response.json() as PlaceDetailsPayload;
  const latitude = payload.location?.latitude;
  const longitude = payload.location?.longitude;

  if (typeof latitude !== 'number' || typeof longitude !== 'number') return null;

  return {
    address: payload.formattedAddress ?? '',
    coordinates: { latitude, longitude },
  };
};

// ── Suggestions cache (LRU, shared across hook instances) ────────────────────

type CacheEntry = { results: AddressSuggestion[]; timestamp: number };

const suggestionsCache = new Map<string, CacheEntry>();

const buildCacheKey = (
  input: string,
  languageCode: string,
  locationRestriction: LocationRestriction,
  includedPrimaryTypes: string[],
  regionCode: string,
  locationBias?: LocationBias,
): string => [
  input,
  languageCode,
  locationBias ? JSON.stringify(locationBias) : JSON.stringify(locationRestriction),
  JSON.stringify(includedPrimaryTypes),
  regionCode,
].join('|');

const getCached = (key: string): AddressSuggestion[] | null => {
  const entry = suggestionsCache.get(key);
  if (!entry) return null;

  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    suggestionsCache.delete(key);
    return null;
  }

  // LRU touch: move to end of Map.
  suggestionsCache.delete(key);
  suggestionsCache.set(key, entry);
  return entry.results;
};

const setCached = (key: string, results: AddressSuggestion[]): void => {
  if (suggestionsCache.size >= MAX_CACHE_SIZE) {
    const oldest = suggestionsCache.keys().next().value;
    if (oldest !== undefined) suggestionsCache.delete(oldest);
  }
  suggestionsCache.set(key, { results, timestamp: Date.now() });
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export const useGooglePlacesAutocomplete = (
  options: UseGooglePlacesAutocompleteOptions,
): UseGooglePlacesAutocompleteReturn => {
  const {
    onAddress,
    onCoordinates,
    onQuotaError,
    languageCode = 'en',
    locationRestriction = DEFAULT_LOCATION_RESTRICTION,
    locationBias,
    includedPrimaryTypes = DEFAULT_INCLUDED_PRIMARY_TYPES,
    regionCode = DEFAULT_REGION_CODE,
    labels: labelOverrides,
  } = options;

  const labels = useMemo(
    () => ({ ...getDefaultLabels(languageCode), ...labelOverrides }),
    [languageCode, labelOverrides],
  );

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ReactNode | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [searchInput, setSearchInput] = useState('');

  const apiKeyRef = useRef(GOOGLE_MAPS_API_KEY);
  const sessionTokenRef = useRef('');
  const suggestionsAbortRef = useRef<AbortController | null>(null);
  const detailsAbortRef = useRef<AbortController | null>(null);
  const onAddressRef = useRef(onAddress);
  const onCoordinatesRef = useRef(onCoordinates);
  const onQuotaErrorRef = useRef(onQuotaError);

  // Keep callback refs current without re-triggering effects.
  useEffect(() => {
    onAddressRef.current = onAddress;
    onCoordinatesRef.current = onCoordinates;
    onQuotaErrorRef.current = onQuotaError;
  }, [onAddress, onCoordinates, onQuotaError]);

  const resetSessionToken = () => {
    sessionTokenRef.current = crypto.randomUUID();
  };

  // Initialise session token.
  useEffect(() => {
    if (!apiKeyRef.current) {
      setStatus('idle');
      setSuggestions([]);
      setError(labels.missingApiKey);
      return;
    }
    resetSessionToken();
    setStatus('ready');
    setError(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced suggestion fetching with cache.
  useEffect(() => {
    const apiKey = apiKeyRef.current;
    if (!apiKey || !sessionTokenRef.current) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    const query = searchInput.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    // Check cache synchronously before debounce — instant results for repeated queries.
    const cacheKey = buildCacheKey(query, languageCode, locationRestriction, includedPrimaryTypes, regionCode, locationBias);
    const cached = getCached(cacheKey);
    if (cached) {
      setSuggestions(cached);
      setError(null);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    suggestionsAbortRef.current?.abort();
    suggestionsAbortRef.current = controller;

    const timeoutId = window.setTimeout(async () => {
      setIsLoading(true);
      try {
        const results = await fetchSuggestions(
          apiKey, query, languageCode, sessionTokenRef.current,
          locationRestriction, includedPrimaryTypes, regionCode, locationBias, controller.signal,
        );
        setCached(cacheKey, results);
        setSuggestions(results);
        setError(null);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof GoogleApiError && err.isQuotaError) {
          onQuotaErrorRef.current?.();
        }
        setError(labels.fetchSuggestionsError);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [searchInput, languageCode, locationRestriction, locationBias, includedPrimaryTypes, regionCode, labels.fetchSuggestionsError]);

  const search = useCallback((input: string) => {
    setSearchInput(input);
    if (input.trim().length === 0) {
      setSuggestions([]);
      onCoordinatesRef.current({ latitude: 0, longitude: 0 }, 'search_bar');
    }
  }, []);

  const selectSuggestion = useCallback(async (suggestion: AddressSuggestion) => {
    const apiKey = apiKeyRef.current;
    if (!apiKey) return;

    setStatus('loading');

    detailsAbortRef.current?.abort();
    const controller = new AbortController();
    detailsAbortRef.current = controller;

    try {
      const details = await fetchPlaceDetails(
        apiKey, suggestion.id, languageCode, sessionTokenRef.current, controller.signal,
      );

      if (!details) {
        setError(labels.missingLocation);
        resetSessionToken();
        return;
      }

      const { latitude, longitude } = details.coordinates;
      const formattedAddress = details.address || suggestion.fullText;

      if (!isWithinRestriction(latitude, longitude, locationRestriction)) {
        setError(labels.outsideArea);
        resetSessionToken();
        return;
      }

      setSuggestions([]);
      onAddressRef.current(formattedAddress);
      onCoordinatesRef.current({ latitude, longitude }, 'search_bar');
      setError(null);
      resetSessionToken();
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof GoogleApiError && err.isQuotaError) {
        onQuotaErrorRef.current?.();
      }
      setError(labels.fetchDetailsError);
    } finally {
      setStatus('ready');
    }
  }, [languageCode, locationRestriction, labels.missingLocation, labels.outsideArea, labels.fetchDetailsError]);

  // Cleanup abort controllers on unmount.
  useEffect(() => {
    return () => {
      suggestionsAbortRef.current?.abort();
      detailsAbortRef.current?.abort();
    };
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    status,
    search,
    selectSuggestion,
  };
};
